import React from 'react';
import { Camera, Mountain, Users, Instagram, X } from 'lucide-react';
import { mockData } from '../utils/mock';
import { useLanguage } from '../context/LanguageContext';

const AffiliateModal = ({ isOpen, onClose }) => {
  const { t, language } = useLanguage();
  const { affiliateProgram, location } = mockData;
  const content = affiliateProgram[language] || affiliateProgram.es;

  const openInstagram = () => window.open(location.links.instagram, '_blank', 'noopener,noreferrer');

  const offerIcons = [
    <Mountain className="w-5 h-5" />,
    <Camera className="w-5 h-5" />,
    <Users className="w-5 h-5" />,
    <Instagram className="w-5 h-5" />
  ];

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: 'rgba(10,10,10,0.92)', backdropFilter: 'blur(10px)' }}
      role="dialog"
      aria-modal="true"
      data-testid="affiliate-modal"
    >
      <div className="min-h-screen py-10 px-4">
        <div className="mx-auto max-w-5xl">
          {/* Header */}
          <div className="flex justify-between items-start mb-10">
            <div>
              <div className="flex items-center" style={{ gap: '14px' }}>
                <span style={{ display: 'block', width: '32px', height: '1px', background: '#C9A84C' }} />
                <span className="font-body uppercase" style={{ fontSize: '11px', letterSpacing: '3px', color: '#C9A84C' }}>Menjin</span>
              </div>
              <h1 className="font-display mt-3" style={{ fontSize: 'clamp(30px, 4vw, 44px)', fontWeight: 700, color: '#F5F5F0' }}>
                {t('affiliate.title')}
              </h1>
            </div>
            <button onClick={onClose} className="text-[#C2C1B6] hover:text-[#C9A84C] transition-colors duration-200 p-2" data-testid="affiliate-close-top" aria-label={t('affiliate.close')}>
              <X className="w-7 h-7" />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-10 items-start">
            {/* Left */}
            <div className="space-y-8">
              <div>
                <h2 className="font-display" style={{ fontSize: '28px', fontWeight: 700, color: '#F5F5F0' }}>{content.title}</h2>
                <p className="font-body mt-3" style={{ fontSize: '15px', fontStyle: 'italic', color: '#C9A84C', lineHeight: 1.6 }}>{content.subtitle}</p>
                <p className="font-body mt-5" style={{ fontSize: '15px', fontWeight: 300, color: '#C2C1B6', lineHeight: 1.9 }}>{content.description}</p>
              </div>

              <div>
                <h3 className="font-body uppercase" style={{ fontSize: '11px', letterSpacing: '3px', color: '#C9A84C', fontWeight: 600 }}>{t('affiliate.whatWeOffer')}</h3>
                <div className="mt-5 space-y-3">
                  {content.offers.map((offer, index) => (
                    <div key={`offer-${index}-${offer.slice(0, 10)}`} className="flex items-start gap-4 p-4" style={{ background: '#1C1C1C', border: '1px solid rgba(201,168,76,0.12)' }}>
                      <div style={{ color: '#C9A84C', marginTop: '2px' }}>{offerIcons[index]}</div>
                      <p className="font-body flex-1" style={{ fontSize: '14px', fontWeight: 300, color: '#C2C1B6' }}>{offer}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ background: '#1C1C1C', border: '1px solid rgba(201,168,76,0.18)', padding: '24px' }}>
                <p className="font-body" style={{ fontSize: '15px', fontWeight: 300, color: '#F5F5F0', lineHeight: 1.8 }}>{content.target}</p>
                <p className="font-body mt-4" style={{ fontSize: '14px', color: '#C9A84C', fontWeight: 600 }}>{content.cta}</p>
              </div>
            </div>

            {/* Right - Contact */}
            <div style={{ background: '#1C1C1C', border: '1px solid rgba(201,168,76,0.18)', padding: '40px' }}>
              <div className="flex items-center justify-center mx-auto" style={{ width: '64px', height: '64px', border: '1px solid #C9A84C', color: '#C9A84C' }}>
                <Instagram className="w-8 h-8" />
              </div>
              <h3 className="font-display text-center mt-6" style={{ fontSize: '24px', fontWeight: 700, color: '#F5F5F0' }}>{t('affiliate.joinProgram')}</h3>
              <p className="font-body text-center mt-3" style={{ fontSize: '14px', fontWeight: 300, color: '#C2C1B6', lineHeight: 1.8 }}>{t('affiliate.joinDescription')}</p>

              <button
                onClick={openInstagram}
                className="font-body uppercase w-full mt-7 inline-flex items-center justify-center transition-colors duration-200"
                style={{ background: '#C8282B', color: '#fff', padding: '15px', fontSize: '12px', letterSpacing: '2px', fontWeight: 600 }}
                onMouseEnter={(e) => (e.currentTarget.style.background = '#9B1F22')}
                onMouseLeave={(e) => (e.currentTarget.style.background = '#C8282B')}
                data-testid="affiliate-instagram-btn"
              >
                <Instagram className="w-5 h-5 mr-2" />
                {t('affiliate.contactInstagram')}
              </button>
              <p className="font-body text-center mt-4" style={{ fontSize: '13px', color: '#C9A84C' }}>@yamauchiramenpr</p>
              <div className="mt-6 p-4" style={{ background: '#141414', border: '1px solid rgba(201,168,76,0.12)' }}>
                <p className="font-body text-center" style={{ fontSize: '13px', fontWeight: 300, color: '#C2C1B6' }}>{t('affiliate.sendMessage')}</p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center mt-12">
            <button
              onClick={onClose}
              className="font-body uppercase inline-flex items-center transition-colors duration-200"
              style={{ border: '1px solid #C9A84C', color: '#C9A84C', padding: '13px 30px', fontSize: '12px', letterSpacing: '2px', fontWeight: 600 }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#C9A84C'; e.currentTarget.style.color = '#0A0A0A'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#C9A84C'; }}
              data-testid="affiliate-close-bottom"
            >
              {t('affiliate.close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliateModal;
